/**
 * 统一的缓存渲染框架
 *
 * 提供通用的缓存渲染机制，减少 text/image/frame 等模块的重复代码。
 * 支持：
 * - 内容缓存 + 变换缓存分离
 * - 缩放容差检测
 * - ImageBitmap 加速
 * - 位置偏移优化
 * - 渲染成本估算（智能决策是否使用缓存）
 */

import { createRasterContext, getCacheByType } from './raster';
import {
  ensureBitmapForCache,
  getBestDrawSource,
  disposeBitmapFromCache,
  type BitmapCacheExtension,
} from './image-bitmap';

// ============================================================================
// 渲染成本估算
// ============================================================================

/**
 * 渲染成本估算配置
 */
export interface RenderCostConfig {
  /** 每个字符的基础渲染成本 */
  charCost: number;
  /** 每个样式 span 的额外成本 */
  styleCost: number;
  /** 每个高亮的额外成本 */
  highlightCost: number;
  /** 文字特效的成本乘数 */
  textFxMultiplier: number;
  /** 渐变填充的成本乘数 */
  gradientFillMultiplier: number;
  /** 描边的成本乘数 */
  strokeMultiplier: number;
  /** Canvas 创建的基础成本 */
  canvasCreateCost: number;
  /** 每像素的内存成本 */
  pixelMemoryCost: number;
  /** drawImage 的基础成本 */
  drawImageCost: number;
  /** ImageBitmap 创建的异步成本（摊销） */
  bitmapCreateCost: number;
}

/**
 * 默认成本配置（基于经验值，单位为相对成本单位）
 */
export const DEFAULT_COST_CONFIG: RenderCostConfig = {
  charCost: 1, // 每个字符的 fillText 调用成本
  styleCost: 5, // 切换样式（font/fillStyle）的成本
  highlightCost: 10, // 绘制高亮区域的成本
  textFxMultiplier: 3, // 文字特效需要额外的 canvas 操作
  gradientFillMultiplier: 2, // 渐变填充比纯色填充更昂贵
  strokeMultiplier: 1.5, // 描边需要额外的绘制
  canvasCreateCost: 50, // 创建 canvas 的固定成本
  pixelMemoryCost: 0.00001, // 内存分配成本（每像素）
  drawImageCost: 5, // drawImage 调用的基础成本
  bitmapCreateCost: 20, // ImageBitmap 创建成本（异步，摊销到多次渲染）
};

/**
 * 渲染成本估算结果
 */
export interface RenderCostEstimate {
  /** 直接渲染成本 */
  directCost: number;
  /** 缓存创建成本（首次） */
  cacheCreateCost: number;
  /** 缓存命中成本（后续） */
  cacheHitCost: number;
  /** 预期缓存命中次数（启发式） */
  expectedHits: number;
  /** 是否建议使用缓存 */
  shouldCache: boolean;
  /** 成本分析详情 */
  breakdown: {
    textComplexity: number;
    styleComplexity: number;
    effectComplexity: number;
    canvasOverhead: number;
    memoryOverhead: number;
  };
}

/**
 * 估算文本节点的直接渲染成本
 */
export function estimateDirectRenderCost(
  node: {
    text?: string;
    styles?: Array<{ selection: [number, number] }> | null;
    highlights?: Array<any> | null;
    textFx?: any;
    fill?: string;
    strokes?: string;
    effect?: any;
  },
  config: RenderCostConfig = DEFAULT_COST_CONFIG
): number {
  const text = node.text || '';
  const charCount = text.length;

  // 基础文本渲染成本
  let cost = charCount * config.charCost;

  // 样式切换成本
  const styleCount = node.styles?.length || 0;
  cost += styleCount * config.styleCost;

  // 高亮渲染成本
  const highlightCount = node.highlights?.length || 0;
  cost += highlightCount * config.highlightCost;

  // 文字特效成本
  if (node.textFx) {
    cost *= config.textFxMultiplier;
  }

  // 渐变填充成本
  if (node.fill && typeof node.fill === 'string' && node.fill.includes('gradient')) {
    cost *= config.gradientFillMultiplier;
  }

  // 描边成本
  if (node.strokes) {
    cost *= config.strokeMultiplier;
  }

  // 特效层成本
  if (node.effect) {
    const effectType = node.effect.type;
    if (effectType === 'stack') {
      const layerCount = node.effect.params?.layers?.length || 1;
      cost *= layerCount;
    } else if (effectType === 'behindLayer') {
      cost *= 2; // 额外绘制一层
    }
  }

  return cost;
}

/**
 * 估算缓存创建成本
 */
export function estimateCacheCreateCost(
  node: { w: number; h: number },
  zoom: number,
  config: RenderCostConfig = DEFAULT_COST_CONFIG
): number {
  const pixelCount = Math.abs(node.w * node.h * zoom * zoom);

  // Canvas 创建成本
  let cost = config.canvasCreateCost;

  // 内存分配成本
  cost += pixelCount * config.pixelMemoryCost;

  // ImageBitmap 创建成本（摊销）
  cost += config.bitmapCreateCost;

  return cost;
}

/**
 * 估算缓存命中时的渲染成本
 */
export function estimateCacheHitCost(
  _node: { w: number; h: number },
  config: RenderCostConfig = DEFAULT_COST_CONFIG
): number {
  // 主要是 drawImage 调用成本
  return config.drawImageCost;
}

/**
 * 估算预期的缓存命中次数
 *
 * 基于启发式规则：
 * - 静态文本（无编辑）预期命中次数高
 * - 频繁变化的文本预期命中次数低
 * - 大型文本块更可能被缓存复用
 */
export function estimateExpectedCacheHits(
  node: {
    text?: string;
    width?: string | number;
    height?: string | number;
  },
  renderContext: any
): number {
  const text = node.text || '';

  // 基础预期命中次数
  let expectedHits = 10;

  // 文本长度影响：长文本更稳定
  if (text.length > 100) {
    expectedHits += 5;
  } else if (text.length < 10) {
    expectedHits -= 3;
  }

  // 是否正在编辑
  const isEditing = renderContext?.providers?.text?.isEditing?.(node);
  if (isEditing) {
    expectedHits = 1; // 编辑时几乎不会命中缓存
  }

  // 固定尺寸的文本更稳定
  if (node.width === 'fixed' && node.height === 'fixed') {
    expectedHits += 3;
  }

  return Math.max(1, expectedHits);
}

/**
 * 综合评估是否应该使用缓存
 *
 * 决策逻辑：
 * 总缓存成本 = 创建成本 + (命中次数 - 1) * 命中成本
 * 总直接成本 = 命中次数 * 直接渲染成本
 *
 * 如果 总缓存成本 < 总直接成本，则使用缓存
 */
export function estimateRenderCost(
  node: {
    text?: string;
    styles?: Array<{ selection: [number, number] }> | null;
    highlights?: Array<any> | null;
    textFx?: any;
    fill?: string;
    strokes?: string;
    effect?: any;
    w: number;
    h: number;
    width?: string | number;
    height?: string | number;
  },
  renderContext: any,
  zoom: number,
  config: RenderCostConfig = DEFAULT_COST_CONFIG
): RenderCostEstimate {
  const directCost = estimateDirectRenderCost(node, config);
  const cacheCreateCost = estimateCacheCreateCost(node, zoom, config);
  const cacheHitCost = estimateCacheHitCost(node, config);
  const expectedHits = estimateExpectedCacheHits(node, renderContext);

  // 计算总成本
  const totalDirectCost = expectedHits * directCost;
  const totalCacheCost = cacheCreateCost + (expectedHits - 1) * cacheHitCost;

  // 决策
  const shouldCache = totalCacheCost < totalDirectCost;

  // 成本分析详情
  const text = node.text || '';
  const breakdown = {
    textComplexity: text.length * config.charCost,
    styleComplexity: (node.styles?.length || 0) * config.styleCost,
    effectComplexity:
      (node.textFx ? directCost * (config.textFxMultiplier - 1) : 0) +
      (node.highlights?.length || 0) * config.highlightCost,
    canvasOverhead: config.canvasCreateCost,
    memoryOverhead: Math.abs(node.w * node.h * zoom * zoom) * config.pixelMemoryCost,
  };

  return {
    directCost,
    cacheCreateCost,
    cacheHitCost,
    expectedHits,
    shouldCache,
    breakdown,
  };
}

/**
 * 快速判断是否应该跳过缓存（低成本渲染）
 *
 * 用于快速路径判断，避免复杂计算
 */
export function shouldSkipCacheForLowCost(
  node: {
    text?: string;
    styles?: Array<any> | null;
    highlights?: Array<any> | null;
    textFx?: any;
    effect?: any;
    w: number;
    h: number;
  },
  zoom: number
): boolean {
  const text = node.text || '';

  // 快速判断条件（满足任一则跳过缓存）：

  // 1. 极短文本（< 5 字符）且无特效
  if (text.length < 5 && !node.textFx && !node.effect) {
    return true;
  }

  // 2. 无样式、无高亮、无特效的简单文本（< 20 字符）
  if (
    text.length < 20 &&
    !node.styles?.length &&
    !node.highlights?.length &&
    !node.textFx &&
    !node.effect
  ) {
    return true;
  }

  // 3. 缓存 canvas 过大（内存成本过高）
  const pixelCount = Math.abs(node.w * node.h * zoom * zoom);
  const MAX_CACHE_PIXELS = 4 * 1024 * 1024; // 4MP
  if (pixelCount > MAX_CACHE_PIXELS) {
    return true;
  }

  // 4. 缓存 canvas 过小（创建开销不值得）
  const MIN_CACHE_PIXELS = 64 * 64; // 64x64
  if (pixelCount < MIN_CACHE_PIXELS && text.length < 50) {
    return true;
  }

  return false;
}

// ============================================================================
// 类型定义
// ============================================================================

/** Canvas 带 release 方法 */
export type CanvasWithRelease = HTMLCanvasElement & { release: () => void };

/** 渲染配置 */
export interface RenderConfig {
  actualWidth: number;
  actualHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  scaleX: number;
  scaleY: number;
}

/** 基础缓存条目 */
export interface BaseCacheEntry extends BitmapCacheExtension {
  /** 内容缓存键 */
  contentKey: string;
  /** 变换版本键 */
  transformKey: string;
  /** 缓存时的缩放值 */
  zoom: number;
  /** 缓存的 canvas */
  canvas: CanvasWithRelease;
  /** 渲染配置 */
  renderConfig: RenderConfig;
  /** 缓存时的节点 X 位置 */
  nodeX: number;
  /** 缓存时的节点 Y 位置 */
  nodeY: number;
  /** 绘制目标 X */
  x: number;
  /** 绘制目标 Y */
  y: number;
  /** 绘制目标宽度 */
  w: number;
  /** 绘制目标高度 */
  h: number;
  /** 释放资源 */
  dispose: () => void;
}

/** 缓存渲染选项 */
export interface CacheRenderOptions<TNode, TCache extends BaseCacheEntry> {
  /** 缓存类型名称 */
  cacheType: string;

  /** 获取内容缓存键 */
  getContentKey: (renderContext: any, node: TNode) => string;

  /** 获取变换缓存键 */
  getTransformKey: (renderContext: any, node: TNode) => string;

  /** 计算缩放值 */
  getZoom: (renderContext: any, node: TNode) => number;

  /** 是否应该跳过缓存（直接渲染） */
  shouldSkipCache?: (renderContext: any, node: TNode) => boolean;

  /** 获取渲染边界（可能与节点边界不同，如文字溢出） */
  getRenderBounds?: (
    renderContext: any,
    node: TNode
  ) => { x: number; y: number; w: number; h: number };

  /** 渲染内容到缓存 canvas */
  renderToCache: (cacheContext: CanvasRenderingContext2D, renderContext: any, node: TNode) => void;

  /** 直接渲染（不使用缓存） */
  renderDirect: (renderContext: any, node: TNode) => void;

  /** 缓存命中后的额外处理 */
  onCacheHit?: (cache: TCache, renderContext: any, node: TNode) => void;

  /** 新缓存创建后的额外处理 */
  onCacheCreated?: (cache: TCache, renderContext: any, node: TNode) => void;

  /** 扩展缓存条目（添加额外字段） */
  extendCache?: (cache: BaseCacheEntry, renderContext: any, node: TNode) => TCache;

  /**
   * 是否启用渲染成本估算
   * 启用后会根据成本分析决定是否使用缓存
   */
  enableCostEstimation?: boolean;

  /**
   * 自定义成本配置
   */
  costConfig?: RenderCostConfig;
}

// ============================================================================
// 工具函数
// ============================================================================

/** 缩放容差检测：缩放比在 5% 范围内视为精确匹配（用于非缩放状态） */
const ZOOM_TOLERANCE_MIN = 0.95;
const ZOOM_TOLERANCE_MAX = 1.05;

/** 缩放时的宽松容差：缩放比在 0.5x - 2x 范围内可复用缓存 */
export const SCALING_ZOOM_TOLERANCE_MIN = 0.1;
export const SCALING_ZOOM_TOLERANCE_MAX = 10.0;

/**
 * 检查缩放是否匹配（精确匹配，用于非缩放状态）
 */
export function isZoomMatch(cacheZoom: number, currentZoom: number): boolean {
  if (!cacheZoom || !currentZoom) return false;
  const ratio = cacheZoom / currentZoom;
  return ratio >= ZOOM_TOLERANCE_MIN && ratio <= ZOOM_TOLERANCE_MAX;
}

/**
 * 检查缩放是否在宽松容差范围内（用于缩放状态）
 * 超出这个范围时，缓存分辨率差异太大，需要重新渲染
 */
export function isZoomInScalingRange(cacheZoom: number, currentZoom: number): boolean {
  if (!cacheZoom || !currentZoom) return false;
  const ratio = cacheZoom / currentZoom;
  return ratio >= SCALING_ZOOM_TOLERANCE_MIN && ratio <= SCALING_ZOOM_TOLERANCE_MAX;
}

/**
 * 获取安全 DPR（确保最小为 2，保证清晰度）
 */
export function getSafeDPR(renderContext: any): number {
  const deviceDPR = renderContext.dpr || 1;
  return Math.max(2, deviceDPR);
}

/**
 * 计算默认缩放值
 */
export function getDefaultZoom(renderContext: any): number {
  const safeDPR = getSafeDPR(renderContext);
  const scale = renderContext.providers?.scale?.scale || 1;
  return scale * safeDPR;
}

/**
 * 设置图像平滑质量
 */
export function setImageSmoothing(
  context: CanvasRenderingContext2D,
  renderContext: any,
  isScaling: boolean
): void {
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = isScaling ? 'low' : renderContext.highQuality ? 'high' : 'medium';
}

/**
 * 检查是否正在缩放
 */
export function isCurrentlyScaling(renderContext: any): boolean {
  return !!renderContext.providers?.scale?.scaling;
}

/**
 * 检查基础缓存有效性（用于非缩放状态）
 */
export function isCacheValid(
  cache: BaseCacheEntry | undefined,
  contentKey: string,
  zoom: number,
  isScaling: boolean
): boolean {
  if (!cache) return false;

  // 缩放时使用 isCacheValidForScaling 来判断
  if (isScaling) return false;

  return (
    cache.contentKey === contentKey &&
    cache.canvas &&
    cache.renderConfig &&
    cache.renderConfig.actualWidth > 0 &&
    cache.renderConfig.actualHeight > 0 &&
    isZoomMatch(cache.zoom, zoom)
  );
}

/**
 * 检查缓存在缩放时是否可复用
 * 缩放时只要内容没变且 zoom 差值在可接受范围内，就可以使用旧缓存
 */
export function isCacheValidForScaling(
  cache: BaseCacheEntry | undefined,
  contentKey: string,
  currentZoom: number
): boolean {
  if (!cache) return false;

  return (
    cache.contentKey === contentKey &&
    cache.canvas &&
    cache.renderConfig &&
    cache.renderConfig.actualWidth > 0 &&
    cache.renderConfig.actualHeight > 0 &&
    isZoomInScalingRange(cache.zoom, currentZoom)
  );
}

/**
 * 绘制缓存到主画布
 */
export function drawCacheToContext(
  context: CanvasRenderingContext2D,
  cache: BaseCacheEntry,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  const drawSource = getBestDrawSource(cache);
  context.drawImage(
    drawSource,
    0,
    0,
    cache.renderConfig.actualWidth,
    cache.renderConfig.actualHeight,
    dx,
    dy,
    dw,
    dh
  );
}

/**
 * 释放缓存资源
 */
export function disposeCache(cache: BaseCacheEntry | undefined): void {
  if (!cache) return;

  disposeBitmapFromCache(cache);
  if (cache.canvas?.release) {
    cache.canvas.release();
  }
}

/**
 * 创建基础缓存条目
 */
export function createBaseCacheEntry(
  contentKey: string,
  transformKey: string,
  zoom: number,
  canvas: CanvasWithRelease,
  renderConfig: RenderConfig,
  node: { x: number; y: number },
  bounds: { x: number; y: number; w: number; h: number }
): BaseCacheEntry {
  const cache: BaseCacheEntry = {
    contentKey,
    transformKey,
    zoom,
    canvas,
    renderConfig,
    nodeX: node.x,
    nodeY: node.y,
    x: bounds.x,
    y: bounds.y,
    w: bounds.w,
    h: bounds.h,
    bitmap: null,
    bitmapPending: false,
    dispose: () => {
      disposeBitmapFromCache(cache);
      cache.canvas?.release();
    },
  };
  return cache;
}

// ============================================================================
// 核心渲染函数
// ============================================================================

/**
 * 通用缓存渲染函数
 *
 * @example
 * ```ts
 * renderWithCache(context, renderContext, node, {
 *   cacheType: 'renderText',
 *   getContentKey: (ctx, n) => ctx.providers.dirty.getContentKey(n.id),
 *   getTransformKey: (ctx, n) => ctx.providers.dirty.getTransformKey(n.id),
 *   getZoom: getDefaultZoom,
 *   shouldSkipCache: (ctx, n) => ctx.mode === 'export',
 *   renderToCache: (cacheCtx, ctx, n) => renderTextRaw({ ...ctx, context: cacheCtx }, n),
 *   renderDirect: (ctx, n) => renderTextRaw(ctx, n),
 *   enableCostEstimation: true, // 启用成本估算
 * });
 * ```
 */
export function renderWithCache<
  TNode extends { id: string; x: number; y: number; w: number; h: number },
  TCache extends BaseCacheEntry = BaseCacheEntry,
>(
  context: CanvasRenderingContext2D,
  renderContext: any,
  node: TNode,
  options: CacheRenderOptions<TNode, TCache>
): void {
  const {
    cacheType,
    getContentKey,
    getTransformKey,
    getZoom,
    shouldSkipCache,
    getRenderBounds,
    renderToCache,
    renderDirect,
    onCacheHit,
    onCacheCreated,
    extendCache,
    enableCostEstimation,
    costConfig,
  } = options;

  // 检查是否应该跳过缓存
  if (shouldSkipCache?.(renderContext, node)) {
    return renderDirect(renderContext, node);
  }

  const { scale, dirty: dirtyProvider } = renderContext.providers || {};
  if (!scale || !dirtyProvider) {
    return renderDirect(renderContext, node);
  }

  const zoom = getZoom(renderContext, node);

  // ========== 渲染成本估算 ==========
  if (enableCostEstimation) {
    // 快速路径：检查是否应该跳过缓存（低成本渲染）
    const textNode = node as TNode & {
      text?: string;
      styles?: Array<any> | null;
      highlights?: Array<any> | null;
      textFx?: any;
      effect?: any;
    };

    if (shouldSkipCacheForLowCost(textNode, zoom)) {
      return renderDirect(renderContext, node);
    }

    // 详细成本估算（仅在非快速路径时计算）
    const costEstimate = estimateRenderCost(textNode, renderContext, zoom, costConfig);
    if (!costEstimate.shouldCache) {
      return renderDirect(renderContext, node);
    }
  }

  const caches = getCacheByType(renderContext, cacheType) as Map<string, TCache>;
  const existCache = caches.get(node.id);
  const isScaling = isCurrentlyScaling(renderContext);

  const contentKey = getContentKey(renderContext, node);
  const transformKey = getTransformKey(renderContext, node);

  // ========== 缩放时使用旧缓存（避免重新渲染） ==========
  if (isScaling && isCacheValidForScaling(existCache, contentKey, zoom)) {
    setImageSmoothing(context, renderContext, true);

    // 计算渲染边界（使用当前节点的实际位置）
    const bounds = getRenderBounds?.(renderContext, node) || {
      x: node.x,
      y: node.y,
      w: node.w,
      h: node.h,
    };

    // 绘制缓存到节点的实际位置和尺寸（保持位置一致，避免漂移）
    drawCacheToContext(context, existCache!, bounds.x, bounds.y, bounds.w, bounds.h);

    onCacheHit?.(existCache!, renderContext, node);
    return;
  }

  // ========== 缓存命中（非缩放状态） ==========
  if (isCacheValid(existCache, contentKey, zoom, isScaling)) {
    setImageSmoothing(context, renderContext, isScaling);

    // 计算位置偏移
    const deltaX = node.x - existCache!.nodeX;
    const deltaY = node.y - existCache!.nodeY;

    // 绘制缓存
    drawCacheToContext(
      context,
      existCache!,
      existCache!.x + deltaX,
      existCache!.y + deltaY,
      existCache!.w,
      existCache!.h
    );

    // 更新位置信息（如果只是位置变化）
    if (existCache!.transformKey !== transformKey) {
      existCache!.nodeX = node.x;
      existCache!.nodeY = node.y;
      existCache!.x += deltaX;
      existCache!.y += deltaY;
      existCache!.transformKey = transformKey;
    }

    onCacheHit?.(existCache!, renderContext, node);
    return;
  }

  // ========== 缓存未命中，重新渲染 ==========
  // 注意：缩放时如果有旧缓存，上面已经处理了；这里只处理真正需要重新渲染的情况

  // 释放旧缓存（只有在非缩放状态或没有可用缓存时才释放）
  if (!isScaling) {
    disposeCache(existCache);
  }

  // 计算渲染边界
  const bounds = getRenderBounds?.(renderContext, node) || {
    x: node.x,
    y: node.y,
    w: node.w,
    h: node.h,
  };

  // 创建 raster 节点
  const rasterNode = {
    ...node,
    x: bounds.x,
    y: bounds.y,
    w: bounds.w,
    h: bounds.h,
  };

  // 创建缓存 canvas
  const {
    canvas,
    context: cacheContext,
    renderConfig,
  } = createRasterContext(renderContext, rasterNode, zoom) as {
    canvas: CanvasWithRelease;
    context: CanvasRenderingContext2D;
    renderConfig: RenderConfig;
  };

  // 创建缓存条目
  let cache = createBaseCacheEntry(
    contentKey,
    transformKey,
    zoom,
    canvas,
    renderConfig,
    node,
    bounds
  ) as TCache;

  // 扩展缓存（添加额外字段）
  if (extendCache) {
    cache = extendCache(cache, renderContext, node);
  }

  // 渲染到缓存
  cacheContext.save();
  renderToCache(cacheContext, renderContext, node);
  cacheContext.restore();

  // 存储缓存
  caches.set(node.id, cache);

  // 绘制到主画布
  setImageSmoothing(context, renderContext, isScaling);
  drawCacheToContext(context, cache, cache.x, cache.y, cache.w, cache.h);

  // 异步创建 ImageBitmap
  ensureBitmapForCache(cache, renderConfig.actualWidth, renderConfig.actualHeight);

  onCacheCreated?.(cache, renderContext, node);
}

// ============================================================================
// 导出便捷函数
// ============================================================================

export {
  getCacheByType,
  createRasterContext,
  ensureBitmapForCache,
  getBestDrawSource,
  disposeBitmapFromCache,
};
